"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, PackageCheck, Truck, ShieldCheck, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="shippingnewvsusedbulls" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mb-12 max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 border border-border bg-muted/60 px-2.5 py-1 text-xs font-mono tracking-wider uppercase text-muted-foreground">
            <Truck className="size-3.5 text-primary" />
            <span>Logistics & Inspection Checklist</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight uppercase font-serif sm:text-4xl lg:text-5xl text-foreground">
            Shipping New vs Used Mechanical Bulls
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            The basic delivery-planning questions apply to both new and used machines, but used equipment can require additional attention to what is actually included with the unit.
          </p>
        </div>

        {/* Comparison Grid */}
        <motion.div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Column 1: New Equipment */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card rounded-none shadow-xs">
              <CardHeader className="border-b border-border bg-muted/30 p-6">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
                    Spec Category 01
                  </span>
                  <Badge variant="outline" className="rounded-none border-border bg-background font-mono text-xs text-foreground uppercase tracking-wide">
                    Factory Spec
                  </Badge>
                </div>
                <div className="mt-2 flex items-center gap-2.5">
                  <PackageCheck className="size-5 text-primary" />
                  <CardTitle className="text-2xl font-bold uppercase tracking-wide font-serif text-foreground">
                    New Equipment
                  </CardTitle>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                <p className="text-sm font-medium leading-normal text-foreground">
                  Check the product package, available shipping information and any manufacturer-specific documentation.
                </p>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Crate & Packaging</span>
                      <p className="mt-0.5 text-sm text-foreground">Standardized factory crating dimensions and known lift-gate freight weight.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Documentation</span>
                      <p className="mt-0.5 text-sm text-foreground">Complete manufacturer schematics, electrical ratings, and operational manuals included.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Transit Insurance</span>
                      <p className="mt-0.5 text-sm text-foreground">Direct freight carrier liability with full replacement value documentation.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 border border-border/80 bg-muted/40 p-3 text-xs font-mono text-muted-foreground">
                  PROTOCOL: VERIFY PALLET FOOTPRINT & ELECTRICAL SERVICE BEFORE DELIVERY DAY.
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Column 2: Used Equipment */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card rounded-none shadow-xs">
              <CardHeader className="border-b border-border bg-muted/30 p-6">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
                    Spec Category 02
                  </span>
                  <Badge variant="outline" className="rounded-none border-primary/40 bg-primary/10 font-mono text-xs text-primary uppercase tracking-wide">
                    Inventory Audit
                  </Badge>
                </div>
                <div className="mt-2 flex items-center gap-2.5">
                  <AlertTriangle className="size-5 text-primary" />
                  <CardTitle className="text-2xl font-bold uppercase tracking-wide font-serif text-foreground">
                    Used Equipment
                  </CardTitle>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                <p className="text-sm font-medium leading-normal text-foreground">
                  Confirm the exact components included with the individual unit, especially the controller, inflatable, blower and accessories.
                </p>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Controller Integrity</span>
                      <p className="mt-0.5 text-sm text-foreground">Verify console model, emergency-stop function, and whether an umbilical cord is bundled.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Inflatable & Blower</span>
                      <p className="mt-0.5 text-sm text-foreground">Ensure matching mattress dimensions, inflation blower specs, and transport tarps are present.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="block text-xs font-mono uppercase text-muted-foreground">Crating Condition</span>
                      <p className="mt-0.5 text-sm text-foreground">Determine if machine is palletized or requires a flatbed with dedicated rigging equipment.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 border border-border/80 bg-muted/40 p-3 text-xs font-mono text-muted-foreground">
                  PROTOCOL: REQUEST PRE-SHIPMENT PHOTOS OF PACKED PALLETS AND LOOSE PARTS.
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Industrial Closing Notice Plate */}
        <motion.div className="mt-8 border border-border bg-card p-6 rounded-none shadow-xs sm:p-8" initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1],
        delay: shouldReduceMotion ? 0 : 0.15
      }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center border border-border bg-muted font-mono text-xs font-bold text-primary">
                !
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">
                  Mandatory Freight Rule
                </span>
                <p className="mt-0.5 text-sm font-medium text-foreground sm:text-base">
                  In either case, base shipping plans on the actual machine rather than a generic equipment profile.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-stretch sm:self-auto border-t sm:border-t-0 sm:border-l border-border pt-3 sm:pt-0 sm:pl-6">
              <HelpCircle className="size-4 text-muted-foreground shrink-0" />
              <span className="text-xs font-mono text-muted-foreground uppercase">
                Commercial Dispatch Std
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}